/**
 * Mixed quarterly-bundle line-up helpers (F2L-3).
 * Each slot is a generic exercise or an org/custom video.
 */

export type BundleLineupItemType = "exercise" | "video";

export interface BundleLineupItem {
  type: BundleLineupItemType;
  ref_id: string;
  position: number;
}

export interface BundleLineupSource {
  lineup_items?: BundleLineupItem[] | null;
  intro_video_id?: string | null;
  exercise_ids?: string[] | null;
}

/** Re-number positions 0..n-1 after reorder / insert / remove. */
export function normalizeLineup(items: BundleLineupItem[]): BundleLineupItem[] {
  return items.map((item, index) => ({
    type: item.type,
    ref_id: item.ref_id,
    position: index,
  }));
}

/** Build lineup from legacy intro + exercise_ids shape. */
export function buildLineupFromLegacy(
  introVideoId: string | null | undefined,
  exerciseIds: string[] | null | undefined,
): BundleLineupItem[] {
  const items: BundleLineupItem[] = [];
  if (introVideoId) {
    items.push({ type: "video", ref_id: introVideoId, position: 0 });
  }
  for (const id of exerciseIds ?? []) {
    if (!id) continue;
    items.push({ type: "exercise", ref_id: id, position: items.length });
  }
  return normalizeLineup(items);
}

/**
 * Derive synced legacy columns from a lineup.
 * intro_video_id = first video slot (if any); exercise_ids = all exercise refs in order.
 */
export function deriveLegacyFromLineup(items: BundleLineupItem[]): {
  intro_video_id: string | null;
  exercise_ids: string[];
} {
  const firstVideo = items.find((i) => i.type === "video");
  return {
    intro_video_id: firstVideo?.ref_id ?? null,
    exercise_ids: items.filter((i) => i.type === "exercise").map((i) => i.ref_id),
  };
}

function isLineupItem(value: unknown): value is BundleLineupItem {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    (v.type === "exercise" || v.type === "video") &&
    typeof v.ref_id === "string" &&
    v.ref_id.length > 0
  );
}

/** Prefer lineup_items when non-empty; otherwise fall back to legacy columns. */
export function resolveBundleLineup(bundle: BundleLineupSource): BundleLineupItem[] {
  const raw = bundle.lineup_items;
  if (Array.isArray(raw) && raw.length > 0) {
    const parsed = raw.filter(isLineupItem).map((item) => ({
      type: item.type,
      ref_id: item.ref_id,
      position: typeof item.position === "number" ? item.position : 0,
    }));
    if (parsed.length > 0) {
      return normalizeLineup([...parsed].sort((a, b) => a.position - b.position));
    }
  }
  return buildLineupFromLegacy(bundle.intro_video_id, bundle.exercise_ids);
}

export function lineupHasPlayableContent(items: BundleLineupItem[]): boolean {
  return items.length > 0;
}
